// react
import {
	FloatingFocusManager,
	FloatingPortal,
	autoUpdate,
	flip,
	offset,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useRole,
} from "@floating-ui/react";
import PropTypes from "prop-types";
import React, { useRef, useState } from "react";

import { faEllipsisV } from "@fortawesome/pro-solid-svg-icons";
import { faEllipsis } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// hooks
import useOutsideClick from "../../hooks/useOutsideClick";

// components
import Text from "../Text";
// styles
import css from "./OptionsDrawer.module.scss";

const OptionsDrawer = (props) => {
	const {
		options,
		onSelect,
		openOn = "left",
		horizontal = false,
		additionalClasses = "",
		optionClasses = "",
		onOpen = () => {},
		optionWidth = 90,
	} = props;

	const ref = useRef();
	const [isOpen, setIsOpen] = useState(false);

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [offset(5), flip({ fallbackAxisSideDirection: "end" }), shift()],
		whileElementsMounted: autoUpdate,
		placement: openOn,
	});

	const click = useClick(context);
	const dismiss = useDismiss(context);
	const role = useRole(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

	useOutsideClick(ref, () => {
		if (isOpen) {
			toggle(false);
		}
	});

	const handleOptionClick = (option) => {
		toggle(false);
		onSelect(option);
	};

	const toggle = (open) => {
		onOpen(open);
		setIsOpen(open);
	};

	return (
		<span ref={ref} className={`${css.main} ${isOpen ? css.isOpen : ""}`}>
			<button
				className={`${css.button} ${additionalClasses}`}
				onClick={() => toggle(!isOpen)}
				data-testid="options-drawer"
				ref={refs.setReference}
				{...getReferenceProps()}
			>
				<FontAwesomeIcon
					icon={horizontal ? faEllipsis : faEllipsisV}
					data-testid={horizontal ? "ellipsis" : "ellipsis-vertical"}
				/>
			</button>

			{isOpen ? (
				<FloatingPortal>
					<FloatingFocusManager context={context} modal={false}>
						<div
							className={`${css.options} ${css[`open-${openOn}`]} ${optionClasses}`}
							style={{ width: optionWidth, ...floatingStyles, zIndex: 1000 }}
							{...getFloatingProps()}
							ref={refs.setFloating}
						>
							{options.map((option) => {
								const { id, label } = option;

								return (
									<button
										key={id}
										data-testid={id}
										onClick={() => handleOptionClick(option)}
										className={css.optionItem}
									>
										<Text size="caption" weight="regular">
											{label}
										</Text>
									</button>
								);
							})}
						</div>
					</FloatingFocusManager>
				</FloatingPortal>
			) : null}
		</span>
	);
};

OptionsDrawer.propTypes = {
	onSelect: PropTypes.func.isRequired,
	options: PropTypes.array.isRequired,
	openOn: PropTypes.string,
	horizontal: PropTypes.bool,
	additionalClasses: PropTypes.string,
	optionClasses: PropTypes.string,
	onOpen: PropTypes.func,
	optionWidth: PropTypes.number,
};

export default OptionsDrawer;
